import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import BalancedDrawDemo from './BalancedDrawDemo';

// A página de quem chega sem conta.
//
// A dobra mostra o balanceamento acontecendo em vez de descrevê-lo: dez
// jogadores com nível, e dois times saindo com o nível médio praticamente
// igual. Prometer "times equilibrados" é o que todo sorteador escreve;
// demonstrar na frente de quem chegou é o que nenhum print faz.
//
// Preço não aparece: não há como comprar antes da Fatia 13, e anunciar valor sem
// caminho de pagamento é um beco sem saída para quem se interessou.

export default function LandingPage() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-16">
      <section className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <h1 className="font-display text-[clamp(2.25rem,7vw,3.75rem)] leading-[1.02] tracking-[-0.03em] text-balance">
            {t('landing.heroTitle')}
          </h1>
          <p className="max-w-xl text-lg leading-relaxed text-chalk-600 dark:text-chalk-400">
            {t('landing.heroBody')}
          </p>
        </div>

        <BalancedDrawDemo />
      </section>

      <Section eyebrow={t('landing.tryEyebrow')} title={t('landing.tryTitle')}>
        <p className="max-w-xl text-chalk-600 dark:text-chalk-400">{t('landing.tryBody')}</p>

        {/* Os três funcionam hoje sem conta. O balanceado é o produto pago e
            nasce aberto até a Fatia 13 (§7) — quando o gate entrar, este cartão
            sai daqui, e a demo do topo passa a ser a única forma de ver o
            balanceamento sem pagar. */}
        <div className="grid gap-3 sm:grid-cols-3">
          <ToolCard to="/nomes" title={t('shell.homeNamesTitle')} body={t('shell.homeNamesBody')} />
          <ToolCard
            to="/elenco"
            title={t('shell.homeBalancedTitle')}
            body={t('shell.homeBalancedBody')}
          />
          <ToolCard to="/placar" title={t('score.title')} body={t('shell.homeScoreBody')} />
        </div>
      </Section>

      <Section eyebrow={t('landing.moreEyebrow')} title={t('landing.moreTitle')}>
        <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          <Feature title={t('landing.featureSquadTitle')} body={t('landing.featureSquadBody')} />
          <Feature title={t('landing.featureAgendaTitle')} body={t('landing.featureAgendaBody')} />
          <Feature title={t('landing.featureRankingTitle')} body={t('landing.featureRankingBody')} />
        </div>
      </Section>

      {/* O caixa ganha seção própria, e não uma quarta caixinha na grade acima:
          é o que faz o organizador largar a planilha, e nasceu de uma planilha
          de vôlei de verdade. */}
      <Section eyebrow={t('landing.moneyEyebrow')} title={t('landing.moneyTitle')}>
        <p className="max-w-xl text-chalk-600 dark:text-chalk-400">{t('landing.moneyBody')}</p>

        <ul className="flex flex-col gap-2 border-l-2 border-turf-600 pl-4 dark:border-turf-400">
          {['moneySplit', 'moneyDebtors', 'moneyRules'].map((key) => (
            <li key={key} className="text-sm text-chalk-600 dark:text-chalk-400">
              {t(`landing.${key}`)}
            </li>
          ))}
        </ul>
      </Section>

      {/* As perguntas são as que aparecem de verdade em grupo de pelada, e as
          respostas são o que o produto faz hoje — não o que ele pretende
          fazer. FAQ que promete vira reclamação depois. */}
      <Section eyebrow={t('landing.faqEyebrow')} title={t('landing.faqTitle')}>
        <dl className="flex flex-col divide-y divide-chalk-200 dark:divide-pitch-700">
          {['accounts', 'size', 'sports', 'install', 'price'].map((key) => (
            <div key={key} className="flex flex-col gap-1 py-4 first:pt-0">
              <dt className="font-semibold">{t(`landing.faq.${key}.q`)}</dt>
              <dd className="text-sm leading-relaxed text-chalk-600 dark:text-chalk-400">
                {t(`landing.faq.${key}.a`)}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* No escuro este bloco tinha a cor exata do fundo da página: o cartão de
          conversão da landing deixava de ser um cartão. */}
      <section className="flex flex-col items-start gap-4 rounded-card bg-pitch-950 p-6 text-chalk-100 sm:p-8 dark:bg-pitch-800">
        <h2 className="font-display text-title sm:text-display">{t('landing.ctaTitle')}</h2>
        <p className="max-w-lg text-chalk-400">{t('landing.ctaBody')}</p>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/criar-conta"
            className="rounded-lg bg-turf-400 px-5 py-2.5 text-sm font-semibold text-pitch-950 transition-colors hover:bg-turf-300"
          >
            {t('landing.ctaButton')}
          </Link>
          <Link to="/entrar" className="text-sm font-medium text-chalk-300 underline underline-offset-4">
            {t('auth.signIn')}
          </Link>
        </div>

        {/* Vem junto do botão, e não escondido no rodapé: o produto se vende
            por confiança, e quem está prestes a subir o elenco do grupo tem o
            direito de saber o que acontece com aquilo antes de clicar. */}
        <p className="max-w-lg text-xs leading-relaxed text-chalk-400">
          {t('landing.trust')}{' '}
          <Link to="/privacidade" className="underline underline-offset-2">
            {t('legal.privacy')}
          </Link>
          .
        </p>
      </section>
    </div>
  );
}

function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <span className="eyebrow text-turf-600 dark:text-turf-400">{eyebrow}</span>
        <h2 className="font-display text-title text-balance sm:text-display">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-chalk-600 dark:text-chalk-400">{body}</p>
    </div>
  );
}

function ToolCard({ to, title, body }: { to: string; title: string; body: string }) {
  return (
    <Link
      to={to}
      className="group flex flex-col gap-1 rounded-card border border-chalk-200 bg-white p-4 transition-colors hover:border-turf-600 dark:border-pitch-700 dark:bg-pitch-900 dark:hover:border-turf-400"
    >
      <span className="flex items-center gap-2 font-semibold">
        {title}
        <span aria-hidden className="text-turf-600 transition-transform group-hover:translate-x-0.5 dark:text-turf-400">
          →
        </span>
      </span>
      <span className="text-sm leading-relaxed text-chalk-600 dark:text-chalk-400">{body}</span>
    </Link>
  );
}
